import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, PlayCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { deleteGeoRun } from "@/lib/api";

// Group runs by YYYY-MM using started_at (fallback completed_at)
function groupRunsByMonth(runs) {
  const byMonth = new Map();
  for (const r of runs) {
    const iso = r.started_at || r.completed_at || r.created_at || "";
    const ym = iso ? String(iso).slice(0, 7) : "sin-fecha";
    if (!byMonth.has(ym)) byMonth.set(ym, []);
    byMonth.get(ym).push(r);
  }
  // Newest first for months + within each month newest first
  const months = [...byMonth.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([ym, list]) => ({
      ym,
      label: ymLabel(ym),
      runs: list.sort((a, b) => String(b.started_at || b.completed_at || "").localeCompare(String(a.started_at || a.completed_at || ""))),
    }));
  return months;
}

function ymLabel(ym) {
  if (!ym || ym === "sin-fecha") return "Sin fecha";
  const [y, m] = ym.split("-");
  const names = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const idx = parseInt(m, 10) - 1;
  return `${names[idx] || m} ${y}`;
}

function runLabel(r) {
  const iso = r.started_at || r.completed_at || r.created_at || "";
  const d = iso ? new Date(iso) : null;
  const dateStr = d ? d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "sin fecha";
  const timeStr = d ? d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "";
  const nPrompts = r.prompts_count ?? r.total_prompts ?? "?";
  const suffix = r.is_snapshot ? " · Día 0" : "";
  return `${dateStr} ${timeStr} · ${nPrompts} prompts${suffix}`;
}

export default function RunSelector({ clientId, runs, selectedRunId, onChange }) {
  const qc = useQueryClient();
  const months = useMemo(() => groupRunsByMonth(runs || []), [runs]);
  const selectedRun = (runs || []).find((r) => r.id === selectedRunId);
  const selectedYm = selectedRun
    ? String(selectedRun.started_at || selectedRun.completed_at || "").slice(0, 7)
    : (months[0]?.ym || "");

  const monthRuns = months.find((m) => m.ym === selectedYm)?.runs || [];

  const handleMonthChange = (ym) => {
    const first = months.find((m) => m.ym === ym)?.runs?.[0];
    if (first) onChange(first.id);
  };

  const del = useMutation({
    mutationFn: () => deleteGeoRun(clientId, selectedRunId),
    onSuccess: () => {
      toast.success("Tirada eliminada");
      // Pick next available run
      const remaining = (runs || []).filter((r) => r.id !== selectedRunId);
      onChange(remaining[0]?.id || null);
      qc.invalidateQueries({ queryKey: ["geo-runs", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-overview", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-evolution", clientId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo eliminar"),
  });

  if (!runs || runs.length === 0) return null;

  return (
    <div className="rounded-md bg-white border border-slate-200 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="run-selector">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
        <Calendar className="h-3.5 w-3.5" />
        <span>Viendo tirada:</span>
      </div>
      <select
        value={selectedYm}
        onChange={(e) => handleMonthChange(e.target.value)}
        className="text-xs px-3 py-1.5 border border-slate-200 rounded bg-white hover:border-slate-300 focus:outline-none focus:border-slate-400"
        data-testid="run-selector-month"
      >
        {months.map((m) => (
          <option key={m.ym} value={m.ym}>
            {m.label} ({m.runs.length} tirada{m.runs.length === 1 ? "" : "s"})
          </option>
        ))}
      </select>
      <select
        value={selectedRunId || ""}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs px-3 py-1.5 border border-slate-200 rounded bg-white hover:border-slate-300 focus:outline-none focus:border-slate-400 min-w-[280px]"
        data-testid="run-selector-run"
      >
        {monthRuns.map((r) => (
          <option key={r.id} value={r.id}>
            {runLabel(r)}
          </option>
        ))}
      </select>
      {selectedRun?.is_snapshot && (
        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold uppercase tracking-wide">
          Snapshot Día 0
        </span>
      )}
      {selectedRun?.status === "running" && (
        <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 font-semibold uppercase tracking-wide inline-flex items-center gap-1">
          <PlayCircle className="h-3 w-3" /> corriendo
        </span>
      )}
      <div className="flex-1" />
      {selectedRun && !selectedRun.is_snapshot && (
        <button
          onClick={() => {
            if (window.confirm(`¿Eliminar esta tirada?\n\n${runLabel(selectedRun)}\n\nSe pierden todos los resultados de este análisis. La acción es irreversible.`)) {
              del.mutate();
            }
          }}
          disabled={del.isPending}
          className="text-[11px] text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-rose-50 disabled:opacity-50"
          data-testid="run-selector-delete"
          title="Eliminar la tirada seleccionada"
        >
          <Trash2 className="h-3 w-3" /> Eliminar tirada
        </button>
      )}
    </div>
  );
}
